import { FragmentDataType } from "@prestosports/prestostats-core/Common";
import React from "react";
import { PassType, RushType } from "@prestosports/prestostats-core/Football";
import PlayerSelector from "./field-selectors/PlayerSelector";
import PenaltyTypeSelector from "./field-selectors/PenaltyTypeSelector";
import TeamSelector from "./field-selectors/TeamSelector";
import TimeSelector from "./field-selectors/TimeSelector";
import TimeAndPeriodSelector from "./field-selectors/TimeAndPeriodSelector";
import PenaltyDownTypeSelector from "./field-selectors/PenaltyDownTypeSelector";
import PatScoreTypeSelector from "./field-selectors/PatScoreTypeSelector";
import FieldSelector from "./field-selectors/FieldSelector";
import EmptySelector from "./field-selectors/EmptySelector";
import { useFootballContext } from "../../../state/football-play/context";
import FieldZonePosition from "./field-selectors/FieldZonePosition";
import PatPointsSelector from "./field-selectors/PatPointsSelector";
import { useGameContextContext } from "../../../state/game-context/context";
import UpdateFieldSelector from "./field-selectors/UpdateFieldSelector";
import GameConfigSelector from "./field-selectors/GameConfigSelector";
import SwapTeamsSelector from "./field-selectors/SwapTeamsSelector";
import { useAppContext } from "../../../state/app/context";
import {
	toCanonicalMs,
	toDisplayMs,
	toDisplaySeconds
} from "./utils/PlayUtils";
import ListSelector from "./field-selectors/ListSelector";
import BooleanSelector from "./field-selectors/BooleanSelector";

const getEnumTypes = (enumType) => {
	return Object.values(enumType)
		.sort((a, b) => a.name.localeCompare(b.name))
		.map((p) => {
			const { code, name } = p;
			let keyStroke = name.match(/\b(\w)/g).join("");
			if (code === "SKIP") {
				keyStroke = "x";
			} else if (code.length) {
				keyStroke = code;
			}
			return Object.assign(p, { keyStroke });
		});
};

const FragmentFieldView = ({
	fragment,
	fragmentField,
	team,
	stepIndex,
	positionPref,
	defaultValue,
	restrictedPositions
}) => {
	const { state, setTimeAndPeriod } = useGameContextContext();
	const gameClock = state.gameClock();
	const disableKeyListener = state.showTimeConfig();
	// gameClock is a revived Clock instance — call .now() for current time.
	const clockNowMs =
		gameClock && typeof gameClock.now === "function"
			? gameClock.now()
			: gameClock?.now ?? 0;
	const { saveFragment, insertTimePlay } = useFootballContext();
	const { name, dependencies } = fragmentField;
	const currentPeriod = state.period();
	const gameConfig = state.gameConfig();
	const { state: appState, setEditPlay } = useAppContext();
	const isInsertingPlay = !!appState.insertPlay();
	const editPlay = appState.editPlay();
	const isSubmitting = appState.isSubmitting();
	const editingPlay = fragment?.playId === editPlay?.id;

	const clearDependencies = (selectedData) => {
		const cleared = selectedData;

		if (dependencies && fragment[name] !== selectedData[name]) {
			dependencies.forEach((d) => {
				cleared[d] = null;
			});
		}

		return cleared;
	};

	const onFragmentDataSelected = (data, moveForward = true) => {
		let selectedData = {
			[name]: data
		};

		selectedData = clearDependencies(selectedData);

		const updated = Object.assign(fragment, selectedData);

		if (editingPlay) {
			setEditPlay({
				...editPlay,
				fragments: [
					...editPlay.fragments.filter((f) => f.id !== updated.id),
					updated
				],
				disabled: updated.requiredData.some(
					(rd) =>
						(rd.type === "Player" && !updated[rd.name]) ||
						(rd.type === "Players" && !updated[rd.name]?.length)
				)
			});
		} else if (!isSubmitting) {
			saveFragment(updated, moveForward ? null : stepIndex);
		}
	};

	const getSelectedValue = () => {
		return (fragment && fragment[name]) || defaultValue;
	};

	const isOptional = () => {
		return fragment.requiredData?.find((rd) => rd.name === name)?.optional;
	};

	const selectorsFactory = {
		[FragmentDataType.Player]: () => {
			return (
				<PlayerSelector
					disableKeyListener={disableKeyListener}
					selected={getSelectedValue()}
					team={team}
					onSelected={(selectedPlayers) => onFragmentDataSelected(selectedPlayers)}
					editingPlay={editingPlay}
					optional={isOptional()}
					positionPref={positionPref}
					defaultValue={defaultValue}
				/>
			);
		},
		[FragmentDataType.Players]: () => {
			return (
				<PlayerSelector
					disableKeyListener={disableKeyListener}
					selected={getSelectedValue()}
					team={team}
					onSelected={(selectedPlayers, maxReached, skipped) => {
						onFragmentDataSelected(selectedPlayers, maxReached || skipped);
					}}
					isMultiselect
					maxAllowed={fragmentField?.maxPlayers || 1}
					editingPlay={editingPlay}
					optional={isOptional()}
					positionPref={positionPref}
					defaultValue={defaultValue}
				/>
			);
		},
		[FragmentDataType.FieldPosition]: () => {
			const showEndZoneYards = !!fragmentField.showEndZoneYards?.(gameConfig);

			return (
				<FieldZonePosition
					disableKeyListener={disableKeyListener}
					position={getSelectedValue()}
					onPositionSelect={onFragmentDataSelected}
					restrictedPositions={restrictedPositions}
					showEndZoneYards={showEndZoneYards}
					defaultValue={defaultValue}
					fragmentRdValue={fragment[name]}
				/>
			);
		},
		[FragmentDataType.PenaltyType]: () => (
			<PenaltyTypeSelector
				disableKeyListener={disableKeyListener}
				selected={getSelectedValue()}
				onSelected={onFragmentDataSelected}
				penalties={fragmentField.getPenaltyTypes?.()}
			/>
		),
		[FragmentDataType.Team]: () => (
			<TeamSelector
				selected={getSelectedValue()}
				onSelected={onFragmentDataSelected}
			/>
		),
		[FragmentDataType.Time]: () => {
			const { milliseconds, period } = getSelectedValue() || {};
			// Time selectors show and collect display time; fragments store
			// canonical countdown (ascending Clock Type is display-only).
			const seedSeconds = milliseconds / 1000 || Math.floor(clockNowMs / 1000);
			return (
				<TimeSelector
					disableKeyListener={disableKeyListener}
					period={period || currentPeriod}
					seconds={toDisplaySeconds(seedSeconds, gameConfig)}
					onSelected={(data) =>
						onFragmentDataSelected({
							...data,
							milliseconds: toCanonicalMs(data.milliseconds, gameConfig)
						})
					}
					hideCancel
					editingPlay={editingPlay}
				/>
			);
		},
		[FragmentDataType.TimeAndPeriod]: () => {
			const selected = getSelectedValue() || {
				milliseconds: Math.floor(clockNowMs),
				period: currentPeriod
			};
			return (
				<TimeAndPeriodSelector
					disableKeyListener={disableKeyListener}
					selected={{
						...selected,
						milliseconds: toDisplayMs(selected.milliseconds, gameConfig)
					}}
					onSelected={(e) =>
						onFragmentDataSelected({
							...e,
							milliseconds: toCanonicalMs(e.milliseconds, gameConfig)
						})
					}
					hideCancel
					editingPlay={editingPlay}
					isInsertingPlay={isInsertingPlay}
				/>
			);
		},
		[FragmentDataType.SetTimeAndPeriod]: () => {
			const selected = getSelectedValue() || {
				milliseconds: Math.floor(clockNowMs),
				period: currentPeriod
			};
			return (
				<TimeAndPeriodSelector
					disableKeyListener={disableKeyListener}
					selected={{
						...selected,
						milliseconds: toDisplayMs(selected.milliseconds, gameConfig)
					}}
					onSelected={(e) => {
						const canonical = {
							...e,
							milliseconds: toCanonicalMs(e.milliseconds, gameConfig)
						};
						const time = {
							seconds: canonical.milliseconds / 1000,
							period: canonical.period
						};
						if (editingPlay) {
							onFragmentDataSelected(canonical);
						} else if (isInsertingPlay) {
							insertTimePlay(time);
						} else {
							setTimeAndPeriod(time);
						}
					}}
					hideCancel
					editingPlay={editingPlay}
					isInsertingPlay={isInsertingPlay}
				/>
			);
		},
		[FragmentDataType.PenaltyDownType]: () => (
			<PenaltyDownTypeSelector
				disableKeyListener={disableKeyListener}
				selected={getSelectedValue()}
				onSelected={onFragmentDataSelected}
			/>
		),
		[FragmentDataType.PatPenaltyDownType]: () => (
			<PenaltyDownTypeSelector
				disableKeyListener={disableKeyListener}
				selected={getSelectedValue()}
				onSelected={onFragmentDataSelected}
				isPat
			/>
		),
		[FragmentDataType.PatScoreType]: () => (
			<PatScoreTypeSelector onSelected={onFragmentDataSelected} />
		),
		[FragmentDataType.PatPoints]: () => (
			<PatPointsSelector
				disableKeyListener={disableKeyListener}
				selected={getSelectedValue()}
				onSelected={onFragmentDataSelected}
			/>
		),
		[FragmentDataType.UpdateContext]: () => (
			<UpdateFieldSelector
				disableKeyListener={disableKeyListener}
				selected={getSelectedValue()}
				onSelected={onFragmentDataSelected}
			/>
		),
		[FragmentDataType.CoinToss]: () => (
			<GameConfigSelector
				disableKeyListener={disableKeyListener}
				selected={getSelectedValue()}
				onSelected={onFragmentDataSelected}
			/>
		),
		[FragmentDataType.SwapTeams]: () => (
			<SwapTeamsSelector
				selected={getSelectedValue()}
				onSelected={onFragmentDataSelected}
			/>
		),
		[FragmentDataType.RushType]: () => (
			<ListSelector
				disableKeyListener={disableKeyListener}
				items={getEnumTypes(RushType)}
				selected={getSelectedValue()}
				onSelected={onFragmentDataSelected}
			/>
		),
		[FragmentDataType.PassType]: () => (
			<ListSelector
				disableKeyListener={disableKeyListener}
				items={getEnumTypes(PassType)}
				selected={getSelectedValue()}
				onSelected={onFragmentDataSelected}
			/>
		),
		[FragmentDataType.Boolean]: () => (
			<BooleanSelector
				disableKeyListener={disableKeyListener}
				selected={getSelectedValue()}
				onSelected={onFragmentDataSelected}
				onSkip={onFragmentDataSelected}
			/>
		)
	};

	const getComponent = (type) => {
		return selectorsFactory[type]();
	};

	const useFragmentFieldComponent = () => {
		const { displayName, type } = fragmentField;

		return (
			(selectorsFactory[type] && (
				<FieldSelector>{getComponent(type)}</FieldSelector>
			)) || <EmptySelector displayName={displayName} />
		);
	};

	return useFragmentFieldComponent();
};

export default FragmentFieldView;
